import { useEffect } from 'react'
import { classes, style, style as _style } from 'typestyle'
import { useLocation, useParams } from 'react-router-dom'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { get, set } from '../../../share/find'

/* Hooks ======================================================================================== */
import { useTheme } from '../../../components/theme'
import useFetch from '../../../hooks/use-fetch'
import useArray from '../../../hooks/use-array'
import useBoolean from '../../../hooks/use-boolean'
import useValue from '../../../hooks/use-value'

/* Components =================================================================================== */
import Resource from '../../../modules/components/resource'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Dropdown } from '../../../components/dropdown'
import { Line } from '../../../components/line'
import { Select } from '../../../components/select'
import { Snippet, SnippetHead } from '../../../modules/layouts/snippet'
import { Notification } from '../../../components/notification'

/* Styles ======================================================================================= */
import { f18i } from '../../../styles/styleset/font-size/f18i'
import { mr8 } from '../../../styles/styleset/margin/mr8'
import { p12md } from '../../../styles/styleset/padding/p12md'
import { p16 } from '../../../styles/styleset/padding/p16'
import { p4 } from '../../../styles/styleset/padding/p4'
import { pb12i } from '../../../styles/styleset/padding/pb12i'
import { pb8i } from '../../../styles/styleset/padding/pb8i'
import { pl16 } from '../../../styles/styleset/padding/pl16'
import { pr16 } from '../../../styles/styleset/padding/pr16'
import { pt12i } from '../../../styles/styleset/padding/pt12i'
import { pt8i } from '../../../styles/styleset/padding/pt8i'
import { w100 } from '../../../styles/styleset/width/w100'
import { p8 } from '../../../styles/styleset/padding/p8'

const HighlightButton = (props: any) => {
  const [toggled, $toggled] = useValue(props.toggled)
  const [currentTheme] = useTheme()

  return (
    <Button
      size="sm"
      baseline="none"
      onClick={() => {
        $toggled.set((prev: any) => {
          props?.onChange?.(!prev)
          return !prev
        })
      }}
      icon={
        <span
          className={classes(
            'material-icons',
            f18i,
            style({
              color: toggled
                ? '#ffcf4d !important'
                : `${currentTheme.baselines.background.color.light} !important`,
            }),
          )}
        >
          star
        </span>
      }
    />
  )
}

export default addSnippet('Team', (props: any) => {
  const { row, $row, params = {}, sync } = props
  const location = useLocation()
  const { groupId } = useParams()
  const { teamKey = 'body.team' } = params

  const [isShowingAll, $isShowingAll] = useBoolean(false)
  const [team, $team] = useArray<object>([])
  const [, $data] = useFetch([], {
    url: process.env.REACT_APP_API_URL + location.pathname,
  })
  const [resources, $resources] = useFetch(
    { data: [] },
    { url: `${process.env.REACT_APP_API_URL}/a/groups/${groupId}/base/resources/table` },
  )
  const [hasUnsaved, $hasUnsaved] = useBoolean(false)
  const [isSaving, $isSaving] = useBoolean(false)
  const [isCancelling, $isCancelling] = useBoolean(false)

  useEffect(() => {
    const teamMembers = get(teamKey, row, 'array')
    $team.set(teamMembers)
  }, [row])

  useEffect(() => {
    if (resources.mounted) {
      $resources.get({
        search: {
          id: {
            $nin: team.map((t: any) => {
              return { id: t.id?.id }
            }),
          },
        },
        lookups: [{ key: 'body.linkTeammate' }],
      })
    }
  }, [resources.mounted])

  return (
    <Snippet>
      <SnippetHead title={'Team'}>
        <Button
          label="Add Team Member"
          baseline="primary"
          size="xs"
          onClick={() => {
            $isShowingAll.set(true)
            $team.push({ id: null })
            $hasUnsaved.set(true)
          }}
          square
        />
      </SnippetHead>
      {team.slice(0, isShowingAll ? team.length : 3).map((teammate: any, key: any) => {
        if (teammate.id?.body?.name) {
          const handleRemove = () => {
            $team.splice(key)
            $isShowingAll.set(true)
            $hasUnsaved.set(true)
          }

          return (
            <Box baseline="none" key={key} row>
              <Line />
              <Box
                className={classes(pb12i, pt12i, _style({ cursor: 'pointer' }), pl16, pr16)}
                baseline="none"
                flex
                fullWidth
              >
                <Box baseline="none" className={classes(_style({ flex: 1 }))}>
                  <Resource resource={teammate} />
                </Box>
                <Box flex alignCenterY>
                  {params.allowHighlight && (
                    <HighlightButton
                      toggled={teammate.toggled}
                      onChange={async (toggled: any) => {
                        if (toggled && params.highlightMessage) {
                          const messageValue = get(params.messageKey, teammate.id)

                          Notification.push({
                            label: params.highlightMessage.replace('{{messageKey}}', messageValue),
                          })
                        }

                        $isSaving.set(true)
                        const body = {
                          replace: teamKey.replace(/^body\./, ''),
                        }
                        const newTeam: any = team
                        newTeam[key].toggled = toggled
                        set(teamKey, body, newTeam)
                        sync()

                        const newRow = await $data.patch({
                          body,
                          search: {
                            lookups: [{ key: `${teamKey}$.id` }],
                          },
                        })

                        $row.set(newRow)
                        $team.set(get(teamKey, newRow, 'array'))
                        $hasUnsaved.set(false)
                        $isSaving.set(false)
                      }}
                    />
                  )}
                  <Dropdown
                    floatProps={{
                      anchorPosition: 'bottom right',
                      dialogPosition: 'top right',
                      width: 'fit-content',
                    }}
                  >
                    <Button
                      size="sm"
                      baseline="none"
                      icon={<span className={classes('material-icons', f18i)}>more_horiz</span>}
                    />
                    {(close: any) => {
                      return (
                        <Box
                          baseline="surface"
                          className={classes(p4, _style({ width: 'fit-content' }))}
                          rounded
                          shadow
                          flex
                          row
                        >
                          <Button
                            className={classes(w100)}
                            baseline="background"
                            label={'Remove'}
                            size="sm"
                            center={false}
                            square
                            onClick={() => {
                              handleRemove()
                              close()
                            }}
                          />
                        </Box>
                      )
                    }}
                  </Dropdown>
                </Box>
              </Box>
            </Box>
          )
        } else {
          return (
            <Box row key={key}>
              <Line />
              <Box row baseline="none" className={classes(pb8i, pt8i, pl16, pr16)}>
                <Select
                  fullWidth
                  options={resources.payload.data}
                  searchEnabled={true}
                  allowEmpty={true}
                  onChange={(data: any) => {
                    team[key] = {
                      id: { id: data.id },
                    }
                    $team.set(team)
                  }}
                  renderValue={(teammate: any) => {
                    const resource = { id: teammate }
                    return <Resource resource={resource} />
                  }}
                  renderOption={(teammate: any) => {
                    const resource = { id: teammate }
                    return (
                      <Box
                        baseline="none"
                        className={classes(
                          p8,
                          style({
                            $nest: {
                              '&:hover': {
                                background: 'rgb(240,240,240)',
                                cursor: 'pointer',
                              },
                            },
                          }),
                        )}
                        style={{ whiteSpace: 'nowrap' }}
                        flex
                        alignCenterY
                        fullWidth
                      >
                        <Resource resource={resource} />
                      </Box>
                    )
                  }}
                  search={(term: string) => {
                    return $resources
                      .get({
                        search: {
                          search: {
                            'body.name': term,
                          },
                          lookups: [
                            {
                              key: 'body.linkTeammate',
                            },
                          ],
                        },
                      })
                      .then((res: any) => res?.data)
                  }}
                />
              </Box>
            </Box>
          )
        }
      })}
      {hasUnsaved ? (
        <>
          <Line />
          <Box className={classes(pb8i, pt8i, pl16, pr16)} baseline="none" flex spaceBetween>
            <Box />
            <Box column>
              <Button
                className={mr8}
                label={isCancelling ? 'Cancelling' : 'Cancel'}
                disabled={isCancelling || isSaving}
                baseline="background"
                size="sm"
                square
                onClick={async () => {
                  $isCancelling.set(true)
                  const newRow = await $data.get({
                    search: {
                      lookups: [{ key: `${teamKey}$.id` }],
                    },
                  })
                  $row.set(newRow)
                  $team.set(get(teamKey, newRow, 'array'))
                  $hasUnsaved.set(false)
                  $isCancelling.set(false)
                }}
              />
              <Button
                label={isSaving ? 'Saving' : 'Save'}
                disabled={isCancelling || isSaving}
                baseline="primary"
                size="sm"
                square
                onClick={async () => {
                  $isSaving.set(true)
                  const body = {
                    replace: teamKey.replace(/^body\./, ''),
                  }
                  const newTeam = team
                    .filter((t: any) => {
                      return { id: t.id?.id }
                    })
                    .map((t: any) => {
                      return { id: t.id?.id }
                    })
                  set(teamKey, body, newTeam)
                  sync()

                  const newRow = await $data.patch({
                    body,
                    search: {
                      lookups: [{ key: `${teamKey}$.id` }],
                    },
                  })

                  $row.set(newRow)
                  $team.set(get(teamKey, newRow, 'array'))
                  $hasUnsaved.set(false)
                  $isSaving.set(false)
                }}
              />
            </Box>
          </Box>
        </>
      ) : (
        team.length > 3 && (
          <>
            <Line />
            <Box className={classes(p12md, p16, pb8i, pt8i)} baseline="none" row>
              {/* </SnippetBody> */}
              <Button
                label={isShowingAll ? 'Collapse' : `Show All ${team.length} Teammates`}
                onClick={$isShowingAll.toggle}
                baseline="surface"
                size="sm"
                square
              />
            </Box>
          </>
        )
      )}
    </Snippet>
  )
})
